import { AnimatePresence, motion } from 'framer-motion'
import { ThumbsUp } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import NextImage from '@/components/NextImage'
import { ReactionListDialog } from '@/components/reactions/reaction-list-dialog'
import { EmojiPicker } from '@/components/ui/emoji-picker'

import { CommentReaction, EmojiData, ReactionType } from '@/types/comment.type'
import { UserProfile } from '@/types/user.type'

interface CommentReactionsProps {
  commentId: number
  reactions: CommentReaction[]
  onReact: (commentId: number, reactionType: ReactionType, emojiData: EmojiData) => void
  currentUser?: UserProfile
  classNameReactionText?: string
}

interface MemeEmojiData {
  id: number
  name: string
  imageUrl: string
}

// Default 5 quick reactions
const defaultReactions: Record<
  string,
  {
    activeColor: string
    hoverBgColor: string
    hoverShadow: string
    activeBgColor: string
    hoverColor: string
    emoji: string
    name: string
  }
> = {
  like: {
    activeColor: 'text-blue-600',
    hoverBgColor: '#eff6ff',
    hoverShadow: '0 0 12px rgba(37, 99, 235, 0.6)',
    activeBgColor: '#dbeafe',
    hoverColor: '#2563eb',
    emoji: '👍',
    name: 'Like',
  },
  love: {
    activeColor: 'text-pink-600',
    hoverBgColor: '#fdf2f8',
    hoverShadow: '0 0 12px rgba(219, 39, 119, 0.6)',
    activeBgColor: '#fce7f3',
    hoverColor: '#db2777',
    emoji: '❤️',
    name: 'Love',
  },
  haha: {
    activeColor: 'text-yellow-600',
    hoverBgColor: '#fefce8',
    hoverShadow: '0 0 12px rgba(217, 119, 6, 0.6)',
    hoverColor: '#ca8a04',
    activeBgColor: '#fef9c3',
    emoji: '😂',
    name: 'Haha',
  },
  wow: {
    activeColor: 'text-yellow-500',
    hoverBgColor: '#fffbeb',
    hoverShadow: '0 0 12px rgba(245, 158, 11, 0.6)',
    activeBgColor: '#fef3c7',
    hoverColor: '#eab308',
    emoji: '😮',
    name: 'Wow',
  },
  sad: {
    activeColor: 'text-purple-600',
    hoverBgColor: '#faf5ff',
    hoverShadow: '0 0 12px rgba(147, 51, 234, 0.6)',
    activeBgColor: '#f3e8ff',
    hoverColor: '#9333ea',
    emoji: '😢',
    name: 'Sad',
  },
}

function CommentReactions({
  commentId,
  reactions,
  onReact,
  currentUser,
  classNameReactionText,
}: CommentReactionsProps) {
  const [showReactionBar, setShowReactionBar] = React.useState(false)
  const [animatingReaction, setAnimatingReaction] = React.useState<string | null>(null)
  const [showReactionList, setShowReactionList] = React.useState(false)

  const reactionBarRef = React.useRef<HTMLDivElement>(null)

  const groupedReactions = React.useMemo(() => {
    const groups = new Map<
      string,
      {
        identifier: string
        reactionType: string
        emojiData?: EmojiData
        count: number
        users: Array<{ id: number; name: string }>
        userReacted: boolean
        isDefault: boolean
      }
    >()

    reactions.forEach(reaction => {
      // Create unique identifier for grouping
      let identifier: string
      let isDefault = false

      if (defaultReactions[reaction.reactionType]) {
        // It's a default reaction
        identifier = reaction.reactionType
        isDefault = true
      } else if (reaction.emojiData?.unicode) {
        // It's a custom emoji
        identifier = reaction.emojiData.unicode
      } else if (reaction.emojiData?.imageUrl) {
        // It's a custom meme
        identifier = reaction.emojiData.imageUrl
      } else {
        // Fallback
        identifier = reaction.reactionType
      }

      const existing = groups.get(identifier)

      if (existing) {
        existing.count++
        existing.users.push({
          id: reaction.user.id,
          name: `${reaction.user.firstName} ${reaction.user.lastName}`,
        })
        if (reaction.user.id === currentUser?.id) {
          existing.userReacted = true
        }
      } else {
        groups.set(identifier, {
          identifier,
          reactionType: reaction.reactionType,
          emojiData: reaction.emojiData,
          count: 1,
          users: [
            {
              id: reaction.user.id,
              name: `${reaction.user.firstName} ${reaction.user.lastName}`,
            },
          ],
          userReacted: reaction.user.id === currentUser?.id,
          isDefault,
        })
      }
    })

    return Array.from(groups.values()).sort((a, b) => {
      if (a.userReacted && !b.userReacted) return -1
      if (!a.userReacted && b.userReacted) return 1
      if (a.isDefault && !b.isDefault) return -1
      if (!a.isDefault && b.isDefault) return 1
      return b.count - a.count
    })
  }, [reactions, currentUser?.id])

  const userActiveReaction = groupedReactions.find(group => group.userReacted)

  const handleDefaultReactionClick = async (reactionType: string) => {
    const config = defaultReactions[reactionType]
    if (!config) return

    const emojiData: EmojiData = {
      unicode: config.emoji,
      shortcode: `:${reactionType}:`,
      name: config.name,
      isCustom: false,
    }

    setAnimatingReaction(reactionType)
    await onReact(commentId, reactionType, emojiData)
    setShowReactionBar(false)

    setTimeout(() => {
      setAnimatingReaction(null)
    }, 800)
  }

  const handleEmojiSelect = async (emoji: string) => {
    const emojiData: EmojiData = {
      unicode: emoji,
      shortcode: `:${emoji}:`,
      name: emoji,
      isCustom: true,
    }

    setAnimatingReaction(emoji)
    await onReact(commentId, `${emoji}`, emojiData)
    setShowReactionBar(false)

    setTimeout(() => {
      setAnimatingReaction(null)
    }, 800)
  }

  const handleCustomEmojiSelect = async (memeData: MemeEmojiData) => {
    const emojiData: EmojiData = {
      unicode: memeData.name,
      imageUrl: memeData.imageUrl,
      shortcode: `${memeData.name}`,
      name: memeData.name,
      isCustom: true,
    }

    setAnimatingReaction(memeData.imageUrl)
    await onReact(commentId, `${memeData.name}`, emojiData)
    setShowReactionBar(false)

    setTimeout(() => {
      setAnimatingReaction(null)
    }, 800)
  }

  const renderReactionDisplay = (
    group: {
      emojiData?: EmojiData
      reactionType: string
      identifier: string
      count: number
      users: Array<{ id: number; name: string }>
    },
    size: 'small' | 'medium' = 'medium'
  ) => {
    const sizeClasses = {
      small: 'w-4 h-4 text-sm',
      medium: 'w-5 h-5 text-base',
    }

    if (group.emojiData?.imageUrl) {
      const sizeImage = {
        small: 18,
        medium: 22,
      }
      return (
        <NextImage
          src={group.emojiData.imageUrl}
          alt={group.emojiData.name || 'custom reaction'}
          width={sizeImage[size]}
          height={sizeImage[size]}
          className="rounded object-cover"
          title={group.emojiData.name || 'custom reaction'}
          unoptimized
        />
      )
    }

    if (group.emojiData?.unicode) {
      return (
        <span
          className={`${sizeClasses[size]} flex items-center justify-center`}
          title={group.emojiData.name || group.emojiData.unicode}
        >
          {group.emojiData.unicode}
        </span>
      )
    }

    const defaultConfig = defaultReactions[group.reactionType]
    if (defaultConfig) {
      return (
        <span className={defaultConfig.activeColor} title={defaultConfig.name}>
          {defaultConfig.emoji}
        </span>
      )
    }

    return <span className={sizeClasses[size]}>{group.identifier}</span>
  }

  const getAnimationVariant = (identifier: string) => {
    if (animatingReaction !== identifier) return {}

    return {
      scale: [1, 1.4, 1],
      transition: { duration: 0.6 },
    }
  }

  return (
    <div className="flex items-center gap-2">
      <div className="relative" ref={reactionBarRef}>
        <AnimatePresence>
          {showReactionBar && (
            <motion.div
              className="absolute bottom-full mb-2 flex items-center bg-white shadow-xl rounded-full py-1 sm:py-2 px-2 sm:px-3 z-20 border border-gray-100"
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95, transition: { duration: 0.15 } }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              onMouseLeave={() => setShowReactionBar(false)}
            >
              {/* Default 5 reactions */}
              {Object.entries(defaultReactions).map(([type, config]) => {
                const userHasThisReaction = groupedReactions.some(
                  group => group.reactionType === type && group.userReacted
                )

                return (
                  <motion.button
                    key={type}
                    onClick={() => handleDefaultReactionClick(type)}
                    className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full mx-0.5 sm:mx-1 transition-colors"
                    style={{
                      backgroundColor: userHasThisReaction ? config.activeBgColor : 'transparent',
                    }}
                    title={config.name}
                    whileHover={{
                      scale: 1.25,
                      backgroundColor: config.hoverBgColor,
                      boxShadow: config.hoverShadow,
                      transition: { duration: 0.2 },
                    }}
                    whileTap={{ scale: 0.9 }}
                    animate={getAnimationVariant(type)}
                  >
                    <span className="text-xl">{config.emoji}</span>
                  </motion.button>
                )
              })}

              <div className="w-px h-6 bg-gray-200 mx-2" />

              <div className="relative">
                <EmojiPicker
                  onEmojiSelect={handleEmojiSelect}
                  onCustomEmojiSelect={handleCustomEmojiSelect}
                  buttonClassName="w-8 h-8 sm:w-10 sm:h-10 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center"
                  className="w-80"
                  modal={false}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-2 rounded-3xl text-xs hover:bg-gray-50 transition-all duration-200"
          onMouseEnter={() => setShowReactionBar(true)}
          onClick={() => setShowReactionBar(prev => !prev)}
          whileHover={{
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: { duration: 0.2 },
          }}
          whileTap={{ scale: 0.95 }}
          style={{
            backgroundColor: userActiveReaction ? '#f8fafc' : 'transparent',
            border: userActiveReaction ? '1px solid #e2e8f0' : '1px solid transparent',
          }}
        >
          {userActiveReaction ? (
            <>
              <motion.span initial={false} animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.4 }}>
                {renderReactionDisplay(userActiveReaction)}
              </motion.span>
            </>
          ) : (
            <>
              <motion.span className="text-gray-700 hover:text-blue-500" whileHover={{ rotate: 10 }}>
                <ThumbsUp className="h-4 w-4 sm:h-5 sm:w-5" />
              </motion.span>
              <span
                className={cn(
                  'text-gray-600 font-medium text-xs sm:text-sm hover:text-blue-500',
                  classNameReactionText
                )}
              >
                React
              </span>
            </>
          )}
        </motion.button>
      </div>

      {/* Display reaction counts */}
      {groupedReactions.length > 0 && (
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="flex -space-x-1 sm:-space-x-1.5">
            {groupedReactions.slice(0, 3).map((group, index) => (
              <motion.div
                key={`${group.identifier}-${index}`}
                className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center shadow-sm border-2 border-white bg-gray-50 cursor-pointer"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 10,
                  delay: index * 0.05,
                }}
                whileHover={{
                  scale: 1.3,
                  zIndex: 10,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transition: { duration: 0.2 },
                }}
                whileTap={{ scale: 0.95 }}
                title={`${group.count} reaction${group.count > 1 ? 's' : ''} - ${group.users.map(u => u.name).join(', ')}`}
              >
                {renderReactionDisplay(group, 'small')}
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-[10px] sm:text-xs font-semibold bg-gray-100 hover:bg-gray-200 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-gray-600 cursor-pointer transition-colors"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={`${reactions.length} total reactions`}
            onClick={() => setShowReactionList(true)}
          >
            {reactions.length}
          </motion.div>
        </div>
      )}

      <ReactionListDialog isOpen={showReactionList} onClose={() => setShowReactionList(false)} reactions={reactions} />
    </div>
  )
}

export default CommentReactions
